// @ts-nocheck
// LEGACY: This file is the legacy version of DoctorInfoRegistrationScreen
// It has type incompatibilities with the new DoctorDataForStep interface
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DoctorInfoRequest,
  University,
  Specialization,
  Practitioner,
  Organization,
  Hospital,
  College,
  Sector,
  HealthCenter,
  RegistrationApiService,
} from "../../registration/api/RegistrationApiService";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import AutocompleteField from "../../../shared/components/ui/AutocompleteField";
import CitySelect from "../../../shared/components/ui/CitySelect";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useDoctorInfoWithAutoRefresh } from "../contexts/DoctorInfoContext";
import { useStepRedirect, skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import {
  CAREER_PROGRESSION_VALUES,
} from "../../../shared/types/models/CareerProgression";
import {
  MINISTRY_VALUES,
} from "../../../shared/types/models/Ministries";
import {
  EmploymentStatus,
  EMPLOYMENT_STATUS_VALUES,
  isEmployed,
} from "../../../shared/types/models/EmploymentStatus";
import {
  MEDICAL_DEGREE_VALUES,
} from "../../../shared/types/models/MedicalDegree";
import {
  LANGUAGE_VALUES,
} from "../../../shared/types/models/Language";
import { convertArabicToWesternNumerals, normalizeNumericInput } from "../../../shared/utils/numberUtils";
import { DoctorType, deriveDoctorType, usesSpecialization } from "../../../shared/utils/doctorType";
import { useUnsavedFormGuard } from "../../../shared/utils/unsavedChanges";

// Sentinel value for the "Other" university option (degree obtained outside Iraq).
// When selected, the doctor types the university name and must upload an Iraqi
// certificate-equivalency document.
const OTHER_UNIVERSITY_ID = "other";

/** Radio options, in the order the syndicate lists them. */
const DOCTOR_TYPE_OPTIONS: { value: Exclude<DoctorType, "">; labelKey: string }[] = [
  { value: "specialist", labelKey: "specialist" },
  { value: "practitioner", labelKey: "practitioner" },
  { value: "higher_diploma", labelKey: "higher_diploma" },
];

// Form interface that matches the DoctorInfoRequest API structure
interface DoctorInfoFormData {
  // Backend-populated fields (display only, not editable):
  full_name: string;
  full_name_ar: string;
  medical_license_number: string;
  medical_license_issued_date: string;
  medical_license_expiry_date: string;
  assossiation_specialization_order_number: string;

  // Personal Information (editable)
  email: string;
  date_of_birth: string;
  sex: "male" | "female" | "";

  // Professional Information (editable)
  career_progression: string;
  years_of_experience: number;
  graduation_year: number;
  medical_degree: string;
  additional_qualifications: string[];

  // Employment Information
  employment_status: string;
  ministry: string;
  organization_id: string;
  hospital_id: string;
  sector_id: string; // UI only - used to filter health_center_id
  health_center_id: string;

  // Address Information (editable)
  governorate: string;
  area_id: string;
  area_name: string;
  area_name_ar: string;
  neighborhood: number;
  street: number;
  house_number: number;

  // Degree and Specialization fields
  graduated_from_id: string;
  graduated_from_other: string; // free-text university name when "Other" (degree from abroad)
  specialization_id: string;
  practition_id: string;
  // نوع الطبيب. "higher_diploma" picks a specialization exactly like "specialist"
  // (a higher diploma is a qualification in a specialty) and differs only in the
  // document asked for later.
  doctor_type: DoctorType;

  // Work/Employment (for university employees)
  work_university_id: string; // UI only - used to filter work_college_id
  work_college_id: string; // Sent to API

  // Clinic Information
  has_another_clinic: boolean | null;
  is_supervising_a_medical_center: boolean | null;

  // Settings
  language: string;
}

// Constants moved outside component to prevent re-creation on each render
const GOVERNORATES = [
  "Al Anbar",
  "Al Muthanna",
  "Al Qadisiyah",
  "Babylon",
  "Baghdad",
  "Basrah",
  "Dhiqar",
  "Diyala",
  "Duhok",
  "Erbil",
  "Karbala",
  "Kirkuk",
  "Maysan",
  "Najaf",
  "Nineveh",
  "Saladin",
  "Sulaymaniyah",
  "Wasit",
];

// Mapping from display name to API value
const mapGovernorateToApiValue = (displayName: string): string | undefined => {
  const governorateMap: Record<string, string> = {
    "Al Anbar": "anbar",
    "Al Muthanna": "muthanna",
    "Al Qadisiyah": "qadisiyah",
    Babylon: "babil",
    Baghdad: "baghdad",
    Basrah: "basra",
    Dhiqar: "dhi_qar",
    Diyala: "diyala",
    Duhok: "dohuk",
    Erbil: "erbil",
    Karbala: "karbala",
    Kirkuk: "kirkuk",
    Maysan: "maysan",
    Najaf: "najaf",
    Nineveh: "nineveh",
    Saladin: "saladin",
    Sulaymaniyah: "sulaymaniyah",
    Wasit: "wasit",
  };
  return governorateMap[displayName];
};

// Mapping from API value to display name
const mapApiValueToGovernorate = (apiValue: string): string => {
  const reverseMap: Record<string, string> = {
    anbar: "Al Anbar",
    muthanna: "Al Muthanna",
    qadisiyah: "Al Qadisiyah",
    babil: "Babylon",
    baghdad: "Baghdad",
    basra: "Basrah",
    dhi_qar: "Dhiqar",
    diyala: "Diyala",
    dohuk: "Duhok",
    erbil: "Erbil",
    karbala: "Karbala",
    kirkuk: "Kirkuk",
    maysan: "Maysan",
    najaf: "Najaf",
    nineveh: "Nineveh",
    saladin: "Saladin",
    sulaymaniyah: "Sulaymaniyah",
    wasit: "Wasit",
  };
  return reverseMap[apiValue?.toLowerCase()] || apiValue;
};

interface DoctorInfoRegistrationScreenProps {
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
}

const DoctorInfoRegistrationScreen: React.FC<
  DoctorInfoRegistrationScreenProps
> = ({ onNext, onBack, onError }) => {
  console.log("🎯 DoctorInfoRegistrationScreen component mounted");

  // Use the automatic step redirect hook
  useStepRedirect();

  const navigate = useNavigate();

  const [formData, setFormData] = useState<DoctorInfoFormData>({
    // Personal Information
    full_name: "",
    full_name_ar: "",
    email: "",
    date_of_birth: "",
    sex: "",

    // Medical License Information
    medical_license_number: "",
    medical_license_issued_date: "",
    medical_license_expiry_date: "",

    // Professional Information
    career_progression: "",
    years_of_experience: 0,
    graduation_year: 0,
    medical_degree: "",
    additional_qualifications: [],
    assossiation_specialization_order_number: "",

    // Employment Information
    employment_status: "",
    ministry: "",
    organization_id: "",
    hospital_id: "",
    sector_id: "", // UI only
    health_center_id: "",

    // Address Information
    governorate: "",
    area_id: "",
    area_name: "",
    area_name_ar: "",
    neighborhood: 0,
    street: 0,
    house_number: 0,

    // Degree and Specialization fields
    graduated_from_id: "",
    graduated_from_other: "",
    specialization_id: "",
    practition_id: "",
    doctor_type: "",

    // Work/Employment (for university employees)
    work_university_id: "", // UI only
    work_college_id: "",

    // Clinic Information
    has_another_clinic: null,
    is_supervising_a_medical_center: null,

    // Settings
    language: "",
  });
  // Ask before a design switch discards typed changes (both designs share this guard).
  useUnsavedFormGuard(formData);

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const hasLoadedData = useRef(false);

  // State for autocomplete data
  const [universities, setUniversities] = useState<University[]>([]);
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [colleges, setColleges] = useState<College[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [healthCenters, setHealthCenters] = useState<HealthCenter[]>([]);
  const [universitiesLoading, setUniversitiesLoading] = useState(false);
  const [specializationsLoading, setSpecializationsLoading] = useState(false);
  const [practitionersLoading, setPractitionersLoading] = useState(false);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [hospitalsLoading, setHospitalsLoading] = useState(false);
  const [collegesLoading, setCollegesLoading] = useState(false);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [healthCentersLoading, setHealthCentersLoading] = useState(false);
  const registrationApiRef = useRef<RegistrationApiService>();

  // Create API instance only once
  if (!registrationApiRef.current) {
    registrationApiRef.current = new RegistrationApiService();
  }
  const registrationApi = registrationApiRef.current;

  const { doctorInfo, refreshDoctorInfo } = useDoctorInfoWithAutoRefresh(false);

  // Load existing doctor info when component mounts
  useEffect(() => {
    // Prevent duplicate API calls in React Strict Mode
    if (hasLoadedData.current) {
      return;
    }

    const loadExistingData = async () => {
      setDataLoading(true);
      hasLoadedData.current = true;

      try {
        // First, load cached data immediately if available from DoctorInfo context
        const cachedDoctorInfo = doctorInfo?.doctor_info;
        if (cachedDoctorInfo) {
          // نوع الطبيب comes from the backend now. Fall back to the old inference
          // (whichever id is filled) for doctors saved before the column existed —
          // it cannot tell دبلوم عالي from متخصص, but those rows predate the choice.
          const doctorType = deriveDoctorType(cachedDoctorInfo);
          const specializationId = cachedDoctorInfo.specialization_id || "";
          const practitionId = cachedDoctorInfo.practition_id || "";

          setFormData((prev) => ({
            ...prev,
            // Ensure all string fields are never null
            full_name: cachedDoctorInfo.full_name || "",
            full_name_ar: cachedDoctorInfo.full_name_ar || "",
            email: cachedDoctorInfo.email || "",
            date_of_birth:
              cachedDoctorInfo.date_of_birth || cachedDoctorInfo.dob || "",
            sex:
              (cachedDoctorInfo.sex?.toLowerCase() as "male" | "female") || "",
            language: cachedDoctorInfo.language || "",
            medical_license_number:
              cachedDoctorInfo.medical_license_number || "",
            medical_license_issued_date:
              cachedDoctorInfo.medical_license_issued_date || "",
            medical_license_expiry_date:
              cachedDoctorInfo.medical_license_expiry_date || "",
            career_progression:
              cachedDoctorInfo.career_progression ||
              cachedDoctorInfo.medical_career ||
              "",
            years_of_experience: cachedDoctorInfo.years_of_experience || 0,
            graduation_year: cachedDoctorInfo.graduation_year || 0,
            medical_degree: cachedDoctorInfo.medical_degree || "",
            additional_qualifications:
              cachedDoctorInfo.additional_qualifications || [],
            assossiation_specialization_order_number:
              cachedDoctorInfo.assossiation_specialization_order_number || "",
            // Employment fields
            employment_status: cachedDoctorInfo.employment_status || "",
            ministry: cachedDoctorInfo.ministry || "",
            organization_id: cachedDoctorInfo.organization_id || "",
            hospital_id: cachedDoctorInfo.hospital_id || "",
            sector_id: cachedDoctorInfo.sector_id || "",
            health_center_id: cachedDoctorInfo.health_center_id || "",
            // Address
            governorate: mapApiValueToGovernorate(
              cachedDoctorInfo.governorate || ""
            ),
            area_id: cachedDoctorInfo.area?.id || "",
            area_name: cachedDoctorInfo.area?.name || "",
            area_name_ar: cachedDoctorInfo.area?.name_ar || "",
            neighborhood: cachedDoctorInfo.neighborhood || 0,
            street: cachedDoctorInfo.street || cachedDoctorInfo.alley || 0,
            house_number: cachedDoctorInfo.house_number || 0,
            graduated_from_id: cachedDoctorInfo.graduated_from_other
              ? OTHER_UNIVERSITY_ID
              : cachedDoctorInfo.graduated_from_id || "",
            graduated_from_other: cachedDoctorInfo.graduated_from_other || "",
            // Each doctor_type reads its own id. The practition used to be read
            // from specialization_id, which is null for a practitioner — so a
            // returning GP found الممارسة blank and had to pick it again.
            specialization_id: usesSpecialization(doctorType) ? specializationId : "",
            practition_id: doctorType === "practitioner" ? practitionId : "",
            doctor_type: doctorType,
            work_university_id: "",
            work_college_id: cachedDoctorInfo.work_college_id || "",
            has_another_clinic: cachedDoctorInfo.has_another_clinic ?? null,
            is_supervising_a_medical_center: cachedDoctorInfo.is_supervising_a_medical_center ?? null,
          }));
          // Set loading to false immediately so doctor can interact
          setDataLoading(false);
        }

        // Load all autocomplete data in parallel
        await Promise.all([
          loadUniversities(),
          loadSpecializations(),
          loadPractitions(),
          loadOrganizations(),
        ]);

        // Then fetch fresh data using DoctorInfo context (with deduplication)
        const freshDoctorInfo = await refreshDoctorInfo();

        if (freshDoctorInfo?.doctor_info) {
          const doctorInfo = freshDoctorInfo.doctor_info;

          // See deriveDoctorType — stored value wins, old rows fall back to the ids.
          const doctorType = deriveDoctorType(doctorInfo);
          const specializationId = doctorInfo.specialization_id || "";
          const practitionId = doctorInfo.practition_id || "";

          setFormData({
            // Personal Information
            full_name: doctorInfo.full_name || "",
            full_name_ar: doctorInfo.full_name_ar || "",
            email: doctorInfo.email || "",
            date_of_birth: doctorInfo.date_of_birth || doctorInfo.dob || "",
            sex: (doctorInfo.sex?.toLowerCase() as "male" | "female") || "male",
            language: doctorInfo.language || "arabic",

            // Medical License Information
            medical_license_number: doctorInfo.medical_license_number || "",
            medical_license_issued_date:
              doctorInfo.medical_license_issued_date || "",
            medical_license_expiry_date:
              doctorInfo.medical_license_expiry_date || "",

            // Professional Information
            career_progression:
              doctorInfo.career_progression || doctorInfo.medical_career || "",
            years_of_experience: doctorInfo.years_of_experience || 0,
            graduation_year:
              doctorInfo.graduation_year || new Date().getFullYear(),
            medical_degree: doctorInfo.medical_degree || "",
            additional_qualifications:
              doctorInfo.additional_qualifications || [],
            assossiation_specialization_order_number:
              doctorInfo.assossiation_specialization_order_number || "",

            // Employment fields
            employment_status: doctorInfo.employment_status || "",
            ministry: doctorInfo.ministry || "",
            organization_id: doctorInfo.organization_id || "",
            hospital_id: doctorInfo.hospital_id || "",
            sector_id: doctorInfo.sector_id || "",
            health_center_id: doctorInfo.health_center_id || "",

            // Address Information
            governorate: mapApiValueToGovernorate(doctorInfo.governorate || ""),
            area_id: doctorInfo.area?.id || "",
            area_name: doctorInfo.area?.name || "",
            area_name_ar: doctorInfo.area?.name_ar || "",
            neighborhood: doctorInfo.neighborhood || 0,
            street: doctorInfo.street || doctorInfo.alley || 0,
            house_number: doctorInfo.house_number || 0,

            // Degree and Specialization fields
            graduated_from_id: doctorInfo.graduated_from_other
              ? OTHER_UNIVERSITY_ID
              : doctorInfo.graduated_from_id || "",
            graduated_from_other: doctorInfo.graduated_from_other || "",
            // Each doctor_type reads its own id. The practition used to be read
            // from specialization_id, which is null for a practitioner — so a
            // returning GP found الممارسة blank and had to pick it again.
            specialization_id: usesSpecialization(doctorType) ? specializationId : "",
            practition_id: doctorType === "practitioner" ? practitionId : "",
            doctor_type: doctorType,

            // Work/Employment (for university employees)
            work_university_id: "",
            work_college_id: doctorInfo.work_college_id || "",

            // Clinic Information
            has_another_clinic: doctorInfo.has_another_clinic ?? null,
            is_supervising_a_medical_center: doctorInfo.is_supervising_a_medical_center ?? null,
          });
        }
        // Note: If no doctor info is returned, formData retains its initial empty state
      } catch (error) {
        console.error("Failed to load doctor info:", error);
        // Form is already populated with cached data, so continue normally
      } finally {
        setDataLoading(false);
      }
    };

    loadExistingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load hospitals and sectors when organization_id is set
  useEffect(() => {
    if (formData.organization_id && formData.organization_id.trim() !== "") {
      loadHospitals(formData.organization_id);
      loadSectors(formData.organization_id);
    }
  }, [formData.organization_id]);

  // Load health centers when sector_id is set
  useEffect(() => {
    if (formData.sector_id && formData.sector_id.trim() !== "") {
      loadHealthCenters(formData.sector_id);
    }
  }, [formData.sector_id]);

  // Derive sector_id from health_center_id when health centers are loaded
  useEffect(() => {
    if (formData.health_center_id && formData.health_center_id.trim() !== "" && !formData.sector_id) {
      const hc = healthCenters.find((h: any) => h.id === formData.health_center_id);
      if (hc && hc.sector_id) {
        setFormData((prev) => ({ ...prev, sector_id: hc.sector_id }));
      }
    }
  }, [formData.health_center_id, healthCenters]);

  // Load colleges and set work_university when work_college_id is set
  useEffect(() => {
    const loadCollegeAndUniversity = async () => {
      if (formData.work_college_id && formData.work_college_id.trim() !== "") {
        console.log(
          "🎓 Loading college data for work_college_id:",
          formData.work_college_id
        );

        // Load all colleges first if not loaded
        if (colleges.length === 0) {
          await loadColleges();
        }

        // Find the college to get its university_id
        const college = colleges.find((c) => c.id === formData.work_college_id);
        if (college && college.university_id) {
          console.log(
            "🎓 Found college, setting work_university_id to:",
            college.university_id
          );
          setFormData((prev) => ({
            ...prev,
            work_university_id: college.university_id,
          }));

          // Load colleges filtered by this university
          loadColleges(college.university_id);
        }
      }
    };

    loadCollegeAndUniversity();
  }, [formData.work_college_id]);

  // Load universities data
  const loadUniversities = async () => {
    setUniversitiesLoading(true);

    try {
      const result = await registrationApi.getUniversities();
      if (result.isSuccess && result.data) {
        setUniversities(result.data);
      } else {
        console.error("Failed to load universities:", result.error);
      }
    } catch (error) {
      console.error("Error loading universities:", error);
    } finally {
      setUniversitiesLoading(false);
    }
  };

  // Load specializations data
  const loadSpecializations = async () => {
    setSpecializationsLoading(true);

    try {
      const result = await registrationApi.getSpecializations();
      if (result.isSuccess && result.data) {
        setSpecializations(result.data);
      } else {
        console.error("Failed to load specializations:", result.error);
      }
    } catch (error) {
      console.error("Error loading specializations:", error);
    } finally {
      setSpecializationsLoading(false);
    }
  };

  // Load practitioners data
  const loadPractitions = async () => {
    setPractitionersLoading(true);

    try {
      const result = await registrationApi.getPractitioners();
      if (result.isSuccess && result.data) {
        setPractitioners(result.data);
      } else {
        console.error("Failed to load practitioners:", result.error);
      }
    } catch (error) {
      console.error("Error loading practitioners:", error);
    } finally {
      setPractitionersLoading(false);
    }
  };

  // Load organizations data (optionally filtered by ministry)
  const loadOrganizations = async (ministry?: string) => {
    setOrganizationsLoading(true);

    try {
      const result = await registrationApi.getOrganizations(1, 99, false, ministry);
      if (result.isSuccess && result.data) {
        setOrganizations(result.data);
        console.log(`✅ Loaded ${result.data.length} organizations${ministry ? ` for ministry: ${ministry}` : ''}`);
      } else {
        console.error("Failed to load organizations:", result.error);
      }
    } catch (error) {
      console.error("Error loading organizations:", error);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  // Load hospitals data (optionally filtered by organization)
  const loadHospitals = async (organizationId?: string) => {
    setHospitalsLoading(true);

    try {
      const result = await registrationApi.getHospitals(organizationId);
      if (result.isSuccess && result.data) {
        setHospitals(result.data);
      } else {
        console.error("Failed to load hospitals:", result.error);
      }
    } catch (error) {
      console.error("Error loading hospitals:", error);
    } finally {
      setHospitalsLoading(false);
    }
  };

  // Load colleges data (optionally filtered by university)
  const loadColleges = async (universityId?: string) => {
    setCollegesLoading(true);

    try {
      const result = await registrationApi.getColleges(universityId);
      if (result.isSuccess && result.data) {
        setColleges(result.data);
      } else {
        console.error("Failed to load colleges:", result.error);
      }
    } catch (error) {
      console.error("Error loading colleges:", error);
    } finally {
      setCollegesLoading(false);
    }
  };

  // Load sectors data (optionally filtered by organization)
  const loadSectors = async (organizationId?: string) => {
    setSectorsLoading(true);

    try {
      const result = await registrationApi.getSectors(organizationId);
      if (result.isSuccess && result.data) {
        setSectors(result.data);
      } else {
        console.error("Failed to load sectors:", result.error);
      }
    } catch (error) {
      console.error("Error loading sectors:", error);
    } finally {
      setSectorsLoading(false);
    }
  };

  // Load health centers data (optionally filtered by sector)
  const loadHealthCenters = async (sectorId?: string) => {
    setHealthCentersLoading(true);

    try {
      const result = await registrationApi.getHealthCenters(sectorId);
      if (result.isSuccess && result.data) {
        setHealthCenters(result.data);
      } else {
        console.error("Failed to load health centers:", result.error);
      }
    } catch (error) {
      console.error("Error loading health centers:", error);
    } finally {
      setHealthCentersLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof DoctorInfoFormData,
    value: string | number | string[] | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Handle cascading filters
    if (field === "organization_id") {
      // When organization changes, reload hospitals and sectors filtered by this organization
      if (typeof value === "string" && value) {
        loadHospitals(value);
        loadSectors(value);
      } else {
        setHospitals([]);
        setSectors([]);
        setHealthCenters([]);
        setFormData((prev) => ({ ...prev, hospital_id: "", sector_id: "", health_center_id: "" }));
      }
    }

    if (field === "sector_id") {
      // When sector changes, reload health centers filtered by this sector
      if (typeof value === "string" && value) {
        loadHealthCenters(value);
      } else {
        setHealthCenters([]);
        setFormData((prev) => ({ ...prev, health_center_id: "" }));
      }
    }

    if (field === "work_university_id") {
      // When work university changes, reload colleges filtered by this university
      if (typeof value === "string" && value) {
        loadColleges(value);
      } else {
        setColleges([]);
        setFormData((prev) => ({ ...prev, work_college_id: "" }));
      }
    }

    if (field === "graduated_from_id" && value !== OTHER_UNIVERSITY_ID) {
      // Switching away from "Other" clears the typed university name
      setFormData((prev) => ({ ...prev, graduated_from_other: "" }));
      setErrors((prev) => ({ ...prev, graduated_from_other: "" }));
    }

    // Handle doctor type changes — clear the id the new type does not use, so a
    // doctor who switches choice cannot leave a stale one behind.
    if (field === "doctor_type") {
      if (value === "specialist" || value === "higher_diploma") {
        setFormData((prev) => ({ ...prev, practition_id: "" }));
        setErrors((prev) => ({ ...prev, practition_id: "" }));
      } else if (value === "practitioner") {
        setFormData((prev) => ({ ...prev, specialization_id: "" }));
        setErrors((prev) => ({ ...prev, specialization_id: "" }));
      }
    }
  };

  // Handle employment status change - clears work-related fields when status changes
  const handleEmploymentStatusChange = (status: string) => {
    // Clear all work-related fields when employment status changes
    setFormData((prev) => ({
      ...prev,
      employment_status: status,
      ministry: "",
      organization_id: "",
      hospital_id: "",
      sector_id: "",
      health_center_id: "",
      work_university_id: "",
      work_college_id: ""
    }));

    // Clear related errors
    setErrors((prev) => ({
      ...prev,
      employment_status: "",
      ministry: "",
      organization_id: "",
      hospital_id: "",
      sector_id: "",
      health_center_id: "",
      work_university_id: "",
      work_college_id: ""
    }));

    // Reset data arrays
    setHospitals([]);
    setSectors([]);
    setHealthCenters([]);
    setColleges([]);
  };

  const handleMinistryChange = (ministry: string) => {
    setFormData((prev) => ({
      ...prev,
      ministry,
      // Clear organization, hospital, sector, and health center when ministry changes
      organization_id: "",
      hospital_id: "",
      sector_id: "",
      health_center_id: ""
    }));

    // Clear related errors
    setErrors((prev) => ({
      ...prev,
      ministry: "",
      organization_id: "",
      hospital_id: "",
      sector_id: "",
      health_center_id: ""
    }));

    // Reload organizations filtered by the new ministry
    if (ministry && ministry.trim() !== "") {
      console.log("🏛️ Ministry changed, reloading organizations for:", ministry);
      loadOrganizations(ministry);
      // Clear hospitals, sectors, and health centers since organization was cleared
      setHospitals([]);
      setSectors([]);
      setHealthCenters([]);
    } else {
      // If ministry is cleared, load all organizations
      loadOrganizations();
      setHospitals([]);
      setSectors([]);
      setHealthCenters([]);
    }
  };

  // Handle date of birth validation on blur to prevent manual typing of invalid years
  const handleDateOfBirthBlur = () => {
    if (!formData.date_of_birth) return;

    const birthDate = new Date(formData.date_of_birth);
    const year = birthDate.getFullYear();
    const today = new Date();
    const maxYear = today.getFullYear() - 24;

    // If year is invalid, reset to nearest valid value
    if (year < 1940) {
      const correctedDate = new Date(1940, birthDate.getMonth(), birthDate.getDate());
      setFormData((prev) => ({
        ...prev,
        date_of_birth: correctedDate.toISOString().split('T')[0]
      }));
      setErrors((prev) => ({
        ...prev,
        date_of_birth: t("year_must_be_1940_or_later")
      }));
    } else if (year > maxYear) {
      const correctedDate = new Date(maxYear, birthDate.getMonth(), birthDate.getDate());
      setFormData((prev) => ({
        ...prev,
        date_of_birth: correctedDate.toISOString().split('T')[0]
      }));
      setErrors((prev) => ({
        ...prev,
        date_of_birth: t("min_age_24")
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation (optional - only validate if email is provided)
    if (formData.email && formData.email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = t("invalid_email");
      }
    }

    // Date of birth validation
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = t("field_required");
    } else {
      const birthDate = new Date(formData.date_of_birth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      // Calculate exact age
      let exactAge = age;
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        exactAge--;
      }

      if (exactAge < 24) {
        newErrors.date_of_birth = t("min_age_24");
      }
    }

    if (!formData.sex) newErrors.sex = t("field_required");

    // Professional Information
    // Career progression only required for specialists
    if (formData.doctor_type === "specialist" && !formData.career_progression)
      newErrors.career_progression = t("field_required");
    if (formData.years_of_experience < 0)
      newErrors.years_of_experience = t("negative_experience");
    if (
      !formData.graduation_year ||
      formData.graduation_year < 1900 ||
      formData.graduation_year > new Date().getFullYear()
    ) {
      newErrors.graduation_year = t("invalid_graduation_year");
    }
    if (!formData.medical_degree)
      newErrors.medical_degree = t("field_required");

    // Degree and Specialization - Required fields
    if (
      !formData.graduated_from_id ||
      formData.graduated_from_id.trim() === ""
    ) {
      newErrors.graduated_from_id = t("field_required");
    } else if (formData.graduated_from_id === OTHER_UNIVERSITY_ID) {
      // "Other" (degree from outside Iraq): require the typed university name
      if (!formData.graduated_from_other || formData.graduated_from_other.trim() === "") {
        newErrors.graduated_from_other = t("field_required");
      }
    }

    // Doctor Type - Required
    if (!formData.doctor_type) {
      newErrors.doctor_type = t("field_required");
    }

    // What the professional field must contain follows doctor_type.
    if (usesSpecialization(formData.doctor_type)) {
      if (
        !formData.specialization_id ||
        formData.specialization_id.trim() === ""
      ) {
        newErrors.specialization_id = t("field_required");
      }
    } else if (formData.doctor_type === "practitioner") {
      if (
        !formData.practition_id ||
        formData.practition_id.trim() === ""
      ) {
        newErrors.practition_id = t("field_required");
      }
    }

    // Employment Status validation - based on employment_status value
    const employmentStatus = formData.employment_status;
    const isEmployedStatus = isEmployed(employmentStatus);

    // Employment status based validation
    if (isEmployedStatus) {
      // Employed statuses require ministry and organization
      if (!formData.ministry) newErrors.ministry = t("field_required");
      if (!formData.organization_id || formData.organization_id.trim() === "") {
        newErrors.organization_id = t("field_required");
      }

      // Validate specific workplace based on employment status
      if (employmentStatus === EmploymentStatus.HOSPITAL_EMPLOYEE) {
        if (!formData.hospital_id || formData.hospital_id.trim() === "") {
          newErrors.hospital_id = t("field_required");
        }
      } else if (employmentStatus === EmploymentStatus.HEALTH_CENTER_EMPLOYEE) {
        if (!formData.health_center_id || formData.health_center_id.trim() === "") {
          newErrors.health_center_id = t("field_required");
        }
      } else if (employmentStatus === EmploymentStatus.SECTOR_EMPLOYEE) {
        if (!formData.sector_id || formData.sector_id.trim() === "") {
          newErrors.sector_id = t("field_required");
        }
      } else if (employmentStatus === EmploymentStatus.UNIVERSITY_EMPLOYEE) {
        if (!formData.work_college_id || formData.work_college_id.trim() === "") {
          newErrors.work_college_id = t("field_required");
        }
      }
    }

    // Address Information - Required fields
    if (!formData.governorate) newErrors.governorate = t("field_required");
    if (!formData.area_id) newErrors.area_id = t("field_required");
    if (formData.neighborhood <= 0)
      newErrors.neighborhood = t("positive_number_required");
    if (formData.street <= 0) newErrors.street = t("positive_number_required");
    if (formData.house_number <= 0)
      newErrors.house_number = t("positive_number_required");

    // Clinic Information - Required fields
    if (formData.has_another_clinic === null)
      newErrors.has_another_clinic = t("field_required");
    if (formData.is_supervising_a_medical_center === null)
      newErrors.is_supervising_a_medical_center = t("field_required");

    console.log("🔍 Validation errors found:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("🚀 [handleSubmit] START - Function called");

    const isValid = validateForm();

    // Debug: Log validation results
    console.log("🔍 [handleSubmit] Form validation result:", isValid);
    console.log("🔍 [handleSubmit] Current form data:", formData);
    console.log("🔍 [handleSubmit] Current errors:", errors);

    if (!isValid) {
      console.log("❌ [handleSubmit] Form validation failed. Errors:", errors);
      return;
    }

    // Prevent submission if data is still loading
    if (dataLoading) {
      console.log(
        "⏳ [handleSubmit] Data still loading, preventing submission"
      );
      return;
    }


    console.log(
      "✅ [handleSubmit] Validation passed, setting loading state..."
    );
    setLoading(true);

    try {
      // Check if employed status for conditional fields
      const employedStatus = isEmployed(formData.employment_status);

      // Map form data to API structure
      // Excludes backend-populated fields that are read-only
      const doctorInfoPayload: DoctorInfoRequest = {
        // Personal Information (editable only)
        sex: formData.sex as "male" | "female",
        email: formData.email && formData.email.trim() !== "" ? formData.email.trim() : undefined,
        date_of_birth: formData.date_of_birth,
        language: formData.language || undefined,

        // Professional Information
        // Career progression only for specialists
        career_progression: formData.doctor_type === "specialist" ? formData.career_progression : undefined,
        years_of_experience: formData.years_of_experience || undefined,
        graduation_year: formData.graduation_year,
        medical_degree: formData.medical_degree,

        // Specialization/Practitioner: send the field this doctor_type uses and
        // explicitly null the others, so switching type clears the old value
        // (undefined is dropped by JSON.stringify and skipped by exclude_unset).
        doctor_type: formData.doctor_type || undefined,
        specialization_id: usesSpecialization(formData.doctor_type)
          ? formData.specialization_id || null
          : null,
        practition_id:
          formData.doctor_type === "practitioner" ? formData.practition_id || null : null,
        // University: a known one (FK) OR "Other" => free-text name for degrees from abroad.
        // Send explicit null for the unused field so the backend CLEARS any stale value
        // (e.g. when switching from "Other" back to a real university). undefined would be
        // dropped by JSON.stringify and skipped by the backend's exclude_unset.
        graduated_from_id:
          formData.graduated_from_id === OTHER_UNIVERSITY_ID ? null : formData.graduated_from_id,
        graduated_from_other:
          formData.graduated_from_id === OTHER_UNIVERSITY_ID
            ? (formData.graduated_from_other?.trim() || "")
            : null,

        // Employment fields - only send if employed
        employment_status: formData.employment_status || undefined,
        ministry: employedStatus ? formData.ministry : undefined,
        organization_id: employedStatus ? formData.organization_id : undefined,

        // Workplace specific fields based on employment status
        hospital_id: formData.employment_status === EmploymentStatus.HOSPITAL_EMPLOYEE ? formData.hospital_id : undefined,
        health_center_id: formData.employment_status === EmploymentStatus.HEALTH_CENTER_EMPLOYEE ? formData.health_center_id : undefined,
        sector_id: formData.employment_status === EmploymentStatus.SECTOR_EMPLOYEE ? formData.sector_id : undefined,
        work_college_id: formData.employment_status === EmploymentStatus.UNIVERSITY_EMPLOYEE ? formData.work_college_id : undefined,

        // Clinic Information
        has_another_clinic: formData.has_another_clinic ?? false,
        is_supervising_a_medical_center: formData.is_supervising_a_medical_center ?? false,

        // Address Information
        governorate: mapGovernorateToApiValue(formData.governorate) || "",
        area_id: formData.area_id,
        neighborhood: formData.neighborhood,
        street: formData.street,
        house_number: formData.house_number,
      };

      console.log("📤 Submitting doctor info to API:", doctorInfoPayload);

      // Send PUT request to update doctor profile
      const result = await registrationApi.updateDoctorProfile(
        doctorInfoPayload
      );

      if (!result.isSuccess) {
        onError(result.error || t('failed_to_update_doctor_information'));
        return;
      }

      console.log("✅ Doctor profile updated successfully");

      // Refresh the cached profile BEFORE navigating so the documents step (and a
      // later back-navigation to this screen) see the just-saved data — including
      // whether the university is "Other" (which drives the equivalency upload).
      // The API's triggerDoctorInfoRefresh() is fire-and-forget; await it here.
      try {
        await refreshDoctorInfo(true);
      } catch (e) {
        console.warn("Doctor info refresh after update failed (continuing):", e);
      }

      // Update registration progress to doctor_documents (document upload)
      localStorage.setItem("registration_progress", "doctor_documents");
      console.log("📈 Updated registration progress to: doctor_documents");

      // Navigate directly to document upload, skip redirect check on destination
      skipNextStepRedirect();
      navigate('/doctor_documents');
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : t('failed_to_update_doctor_information')
      );
    } finally {
      setLoading(false);
    }
  };

  const { t, isRTL } = useLanguage();

  // Show loading spinner while data is loading
  if (dataLoading) {
    return (
      <ResponsiveFormLayout
        onBack={onBack}
        showBackButton={false}
        showLanguageToggle={true}
        icon="/ima_logo.png"
        iconSize="w-32 h-32"
        title={t("doctor_information")}
        subtitle={t("doctor_info_subtitle")}
        progressValue={25}
        maxHeight="80vh"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2E5D7D] border-t-transparent"></div>
          <span className="ml-3 text-gray-600">{t("loading_doctor_info")}</span>
        </div>
      </ResponsiveFormLayout>
    );
  }

  const actionButtons = (
    <div className="flex flex-col gap-3 w-full">
      <button
        onClick={handleSubmit}
        disabled={loading || dataLoading}
        className={`w-full min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading || dataLoading
            ? "opacity-50 cursor-not-allowed"
            : Object.keys(errors).filter(key => errors[key]).length > 0
            ? "bg-red-500 hover:bg-red-600 cursor-pointer shadow-md hover:shadow-lg"
            : "hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg"
        }`}
        style={{
          backgroundColor:
            loading || dataLoading
              ? "#2E5D7D"
              : Object.keys(errors).filter(key => errors[key]).length > 0
              ? "#dc2626"
              : "#2E5D7D",
        }}
      >
        {loading || dataLoading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            <span>{dataLoading ? t("loading_data") : t("loading")}</span>
          </div>
        ) : Object.keys(errors).filter(key => errors[key]).length > 0 ? (
          <span>
            {t('fix_errors_to_continue')
              .replace('{count}', String(Object.keys(errors).filter(key => errors[key]).length))
              .replace('{plural}', Object.keys(errors).filter(key => errors[key]).length > 1 ? "s" : "")}
          </span>
        ) : (
          <span>{t("continue")}</span>
        )}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      showLanguageToggle={true}
      icon="/ima_logo.png"
      iconSize="w-32 h-32"
      title={t("doctor_information")}
      subtitle={t("personal_work_info")}
      actions={actionButtons}
      maxHeight="80vh"
    >
      <div className="w-full space-y-6">
        {/* Form Fields */}
        <div className="space-y-6">
          {/* Personal Information Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {t("personal_information")}
            </h3>

            {/* Display-only Full Name Section */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <label
                className={`block text-sm font-medium text-gray-600 mb-1 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("full_name")}
              </label>
              <div className="flex flex-col">
                {formData.full_name_ar && (
                  <p className="text-gray-800 font-medium text-lg" dir="rtl">
                    {formData.full_name_ar}
                  </p>
                )}
                <p className="text-gray-800 font-medium text-lg">
                  {formData.full_name || "-"}
                </p>
              </div>
            </div>

            {/* Email and Date of Birth */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("email")}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.email
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("date_of_birth")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    handleInputChange("date_of_birth", e.target.value)
                  }
                  onBlur={handleDateOfBirthBlur}
                  min="1940-01-01"
                  max={(() => {
                    const today = new Date();
                    const maxDate = new Date(today.getFullYear() - 24, today.getMonth(), today.getDate());
                    return maxDate.toISOString().split('T')[0];
                  })()}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.date_of_birth
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.date_of_birth && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.date_of_birth}
                  </p>
                )}
              </div>
            </div>

            {/* Gender and Language */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("gender")} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.sex}
                  onChange={(e) =>
                    handleInputChange(
                      "sex",
                      e.target.value as "male" | "female"
                    )
                  }
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.sex
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                >
                  <option value="">{t("select_gender")}</option>
                  <option value="male">{t("male")}</option>
                  <option value="female">{t("female")}</option>
                </select>
                {errors.sex && (
                  <p className="text-red-500 text-sm mt-1">{errors.sex}</p>
                )}
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("language")}
                </label>
                <select
                  value={formData.language}
                  onChange={(e) =>
                    handleInputChange("language", e.target.value)
                  }
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.language
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                >
                  <option value="">{t("select_language")}</option>
                  {LANGUAGE_VALUES.map((language) => (
                    <option key={language} value={language}>
                      {t(language)}
                    </option>
                  ))}
                </select>
                {errors.language && (
                  <p className="text-red-500 text-sm mt-1">{errors.language}</p>
                )}
              </div>
            </div>
          </div>

          {/* Medical License Information Section (Display Only) */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {t("medical_license_info")}
            </h3>

            {/* Display-only License Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-white rounded-lg">
              <div>
                <label
                  className={`block text-sm font-medium text-gray-600 mb-1 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("medical_license_number")}
                </label>
                <p className="text-gray-800 font-medium">
                  {formData.medical_license_number || "-"}
                </p>
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-600 mb-1 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("license_issued_date")}
                </label>
                {/* Read-only: sourced from the IMA ID (not doctor-editable). */}
                <p className="text-gray-800 font-medium">
                  {formData.medical_license_issued_date || "-"}
                </p>
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-600 mb-1 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("license_expiry_date")}
                </label>
                <p className="text-gray-800 font-medium">
                  {formData.medical_license_expiry_date || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Education Section */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {t("education_information")}
            </h3>

            {/* Graduated From University and Graduation Year */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <AutocompleteField
                  label={t("graduated_from_university")}
                  placeholder={t("search_select_university")}
                  value={formData.graduated_from_id}
                  options={[
                    ...universities.map((university) => ({
                      id: university.id,
                      name: university.name,
                      name_ar: university.name_ar,
                      code: university.code,
                      country: university.country,
                      city: university.city,
                    })),
                    {
                      id: OTHER_UNIVERSITY_ID,
                      name: t("other_university"),
                      name_ar: t("other_university"),
                    },
                  ]}
                  onChange={(selectedId) =>
                    handleInputChange("graduated_from_id", selectedId)
                  }
                  error={errors.graduated_from_id}
                  loading={universitiesLoading}
                  required
                />
                {formData.graduated_from_id === OTHER_UNIVERSITY_ID && (
                  <div className="mt-3">
                    <label
                      className={`block text-sm font-medium text-gray-700 mb-2 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {t("university_name")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.graduated_from_other}
                      onChange={(e) =>
                        handleInputChange("graduated_from_other", e.target.value)
                      }
                      placeholder={t("enter_university_name")}
                      className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                        errors.graduated_from_other
                          ? "border-red-500"
                          : "border-gray-300 focus:border-blue-500"
                      }`}
                    />
                    {errors.graduated_from_other && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.graduated_from_other}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {t("equivalency_certificate_hint")}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("graduation_year")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.graduation_year}
                  onChange={(e) => {
                    const converted = normalizeNumericInput(e.target.value);
                    handleInputChange(
                      "graduation_year",
                      parseInt(converted) || 0
                    );
                  }}
                  placeholder={t("graduation_year_placeholder") || "1990"}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.graduation_year
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.graduation_year && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.graduation_year}
                  </p>
                )}
              </div>
            </div>

            {/* Medical Degree */}
            <div className="mb-4">
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("medical_degree")} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.medical_degree}
                onChange={(e) =>
                  handleInputChange("medical_degree", e.target.value)
                }
                className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                  errors.medical_degree
                    ? "border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
              >
                <option value="">{t("select_medical_degree")}</option>
                {MEDICAL_DEGREE_VALUES.map((degree) => (
                  <option key={degree} value={degree}>
                    {t(degree)}
                  </option>
                ))}
              </select>
              {errors.medical_degree && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.medical_degree}
                </p>
              )}
            </div>
          </div>

          {/* Career Section */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {t("career_information")}
            </h3>

            {/* Doctor Type Selection */}
            <div className="mb-4">
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("doctor_type")} <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {DOCTOR_TYPE_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="doctor_type"
                      value={option.value}
                      checked={formData.doctor_type === option.value}
                      onChange={(e) =>
                        handleInputChange("doctor_type", e.target.value)
                      }
                      className="w-4 h-4 text-[#2E5D7D] border-gray-300 focus:ring-[#2E5D7D] cursor-pointer"
                    />
                    <span className={`text-sm font-medium text-gray-700 ${isRTL ? "mr-2" : "ml-2"}`}>
                      {t(option.labelKey)}
                    </span>
                  </label>
                ))}
              </div>
              {errors.doctor_type && (
                <p className="text-red-500 text-sm mt-1">{errors.doctor_type}</p>
              )}
            </div>

            {/* Specialization/Practitioner */}
            <div className="mb-4">
              {usesSpecialization(formData.doctor_type) && (
                <AutocompleteField
                  label={t("specialization")}
                  placeholder={t("search_select_specialization")}
                  value={formData.specialization_id}
                  options={specializations.map((spec) => ({
                    id: spec.id,
                    name: spec.name,
                    name_ar: spec.name_ar,
                    code: spec.code,
                    category: spec.category,
                    parent_id: spec.parent_id,
                  }))}
                  onChange={(selectedId) =>
                    handleInputChange("specialization_id", selectedId)
                  }
                  error={errors.specialization_id}
                  loading={specializationsLoading}
                  required
                />
              )}
              {formData.doctor_type === "practitioner" && (
                <AutocompleteField
                  label={t("practitioner")}
                  placeholder={t("search_select_practitioner")}
                  value={formData.practition_id}
                  options={practitioners.map((prac) => ({
                    id: prac.id,
                    name: prac.name,
                    name_ar: prac.name_ar,
                    code: prac.code,
                    category: prac.category,
                    parent_id: prac.parent_id,
                  }))}
                  onChange={(selectedId) =>
                    handleInputChange("practition_id", selectedId)
                  }
                  error={errors.practition_id}
                  loading={practitionersLoading}
                  required
                />
              )}
            </div>

            {/* Career Progression and Years of Experience */}
            <div className={`grid grid-cols-1 ${formData.doctor_type === "specialist" ? "md:grid-cols-2" : ""} gap-4 mb-4`}>
              {/* Career Progression - Only for Specialists */}
              {formData.doctor_type === "specialist" && (
                <div>
                  <label
                    className={`block text-sm font-medium text-gray-700 mb-2 ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {t("career_progression")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.career_progression}
                    onChange={(e) =>
                      handleInputChange("career_progression", e.target.value)
                    }
                    className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                      errors.career_progression
                        ? "border-red-500"
                        : "border-gray-300 focus:border-blue-500"
                    }`}
                  >
                    <option value="">{t("select_career_progression")}</option>
                    {CAREER_PROGRESSION_VALUES.map((career) => (
                      <option key={career} value={career}>
                        {t(career)}
                      </option>
                    ))}
                  </select>
                  {errors.career_progression && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.career_progression}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("years_of_experience")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.years_of_experience}
                  onChange={(e) => {
                    const converted = normalizeNumericInput(e.target.value);
                    handleInputChange(
                      "years_of_experience",
                      parseInt(converted) || 0
                    );
                  }}
                  placeholder={t("years_of_experience_placeholder") || "0"}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.years_of_experience
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.years_of_experience && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.years_of_experience}
                  </p>
                )}
              </div>
            </div>

            {/* Association Specializations Order Number (Display Only) */}
            {formData.assossiation_specialization_order_number && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label
                  className={`block text-sm font-medium text-gray-600 mb-1 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("association_specializations_order_number")}
                </label>
                <p className="text-gray-800 font-medium">
                  {formData.assossiation_specialization_order_number}
                </p>
              </div>
            )}

            {/* Employment Status */}
            <div className="mb-4">
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("employment_status")}
              </label>
              <select
                value={formData.employment_status}
                onChange={(e) => handleEmploymentStatusChange(e.target.value)}
                className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                  errors.employment_status
                    ? "border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
              >
                <option value="">{t("select_employment_status")}</option>
                {EMPLOYMENT_STATUS_VALUES.map((status) => (
                  <option key={status} value={status}>
                    {t(status)}
                  </option>
                ))}
              </select>
              {errors.employment_status && (
                <p className="text-red-500 text-sm mt-1">{errors.employment_status}</p>
              )}
            </div>

            {/* Employment Details - Only show if employed status is selected */}
            {isEmployed(formData.employment_status) && (
              <>
                {/* Ministry */}
                <div className="mb-4">
                  <label
                    className={`block text-sm font-medium text-gray-700 mb-2 ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {t("ministry")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.ministry}
                    onChange={(e) => handleMinistryChange(e.target.value)}
                    className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                      errors.ministry
                        ? "border-red-500"
                        : "border-gray-300 focus:border-blue-500"
                    }`}
                  >
                    <option value="">{t("select_ministry")}</option>
                    {MINISTRY_VALUES.map((ministry) => (
                      <option key={ministry} value={ministry}>
                        {t(ministry)}
                      </option>
                    ))}
                  </select>
                  {errors.ministry && (
                    <p className="text-red-500 text-sm mt-1">{errors.ministry}</p>
                  )}
                </div>

            {/* Organization */}
            <div className="mb-4">
              <AutocompleteField
                label={t("organization")}
                placeholder={t("search_select_organization")}
                value={formData.organization_id}
                options={organizations.map((org) => ({
                  id: org.id,
                  name: org.name,
                  name_ar: org.name_ar,
                  code: org.code,
                }))}
                onChange={(selectedId) =>
                  handleInputChange("organization_id", selectedId)
                }
                error={errors.organization_id}
                loading={organizationsLoading}
                required
              />
            </div>

                {/* Hospital Employee Fields */}
                {formData.employment_status === EmploymentStatus.HOSPITAL_EMPLOYEE && (
                  <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <AutocompleteField
                      label={t("hospital")}
                      placeholder={t("search_select_hospital")}
                      value={formData.hospital_id}
                      options={hospitals.map((hospital) => ({
                        id: hospital.id,
                        name: hospital.name,
                        name_ar: hospital.name_ar,
                        code: hospital.code,
                        organization_id: hospital.organization_id,
                      }))}
                      onChange={(selectedId) =>
                        handleInputChange("hospital_id", selectedId)
                      }
                      error={errors.hospital_id}
                      loading={hospitalsLoading}
                      required
                    />
                  </div>
                )}

                {/* Health Center Employee Fields */}
                {formData.employment_status === EmploymentStatus.HEALTH_CENTER_EMPLOYEE && (
                  <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <AutocompleteField
                        label={t("sector")}
                        placeholder={t("search_select_sector")}
                        value={formData.sector_id}
                        options={sectors.map((sector) => ({
                          id: sector.id,
                          name: sector.name,
                          name_ar: sector.name_ar,
                          code: sector.code || "",
                          organization_id: sector.organization_id,
                        }))}
                        onChange={(selectedId) =>
                          handleInputChange("sector_id", selectedId)
                        }
                        error={errors.sector_id}
                        loading={sectorsLoading}
                        required
                      />
                      <AutocompleteField
                        label={t("health_center")}
                        placeholder={t("search_select_health_center")}
                        value={formData.health_center_id}
                        options={healthCenters.map((hc) => ({
                          id: hc.id,
                          name: hc.name,
                          name_ar: hc.name_ar,
                          code: hc.code || "",
                          sector_id: hc.sector_id,
                        }))}
                        onChange={(selectedId) =>
                          handleInputChange("health_center_id", selectedId)
                        }
                        error={errors.health_center_id}
                        loading={healthCentersLoading}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Sector Employee Fields (like Health Center Employee, but without choosing a Health Center) */}
                {formData.employment_status === EmploymentStatus.SECTOR_EMPLOYEE && (
                  <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <AutocompleteField
                      label={t("sector")}
                      placeholder={t("search_select_sector")}
                      value={formData.sector_id}
                      options={sectors.map((sector) => ({
                        id: sector.id,
                        name: sector.name,
                        name_ar: sector.name_ar,
                        code: sector.code || "",
                        organization_id: sector.organization_id,
                      }))}
                      onChange={(selectedId) =>
                        handleInputChange("sector_id", selectedId)
                      }
                      error={errors.sector_id}
                      loading={sectorsLoading}
                      required
                    />
                  </div>
                )}

                {/* University Employee Fields */}
                {formData.employment_status === EmploymentStatus.UNIVERSITY_EMPLOYEE && (
                  <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <AutocompleteField
                        label={t("work_university")}
                        placeholder={t("search_select_work_university")}
                        value={formData.work_university_id}
                        options={universities.map((university) => ({
                          id: university.id,
                          name: university.name,
                          name_ar: university.name_ar,
                          code: university.code,
                          country: university.country,
                          city: university.city,
                        }))}
                        onChange={(selectedId) =>
                          handleInputChange("work_university_id", selectedId)
                        }
                        error={errors.work_university_id}
                        loading={universitiesLoading}
                        required
                      />
                      <AutocompleteField
                        label={t("work_college")}
                        placeholder={t("search_select_work_college")}
                        value={formData.work_college_id}
                        options={colleges.map((college) => ({
                          id: college.id,
                          name: college.name,
                          name_ar: college.name_ar,
                          code: college.code,
                          university_id: college.university_id,
                        }))}
                        onChange={(selectedId) =>
                          handleInputChange("work_college_id", selectedId)
                        }
                        error={errors.work_college_id}
                        loading={collegesLoading}
                        required
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Address Information Section */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {t("address_information")}
            </h3>

            {/* Governorate and City */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("governorate")} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.governorate}
                  onChange={(e) => {
                    handleInputChange("governorate", e.target.value);
                    handleInputChange("area_id", "");
                  }}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.governorate
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                >
                  <option value="">{t("select_governorate")}</option>
                  {GOVERNORATES.map((gov) => (
                    <option key={gov} value={gov}>
                      {t(gov)}
                    </option>
                  ))}
                </select>
                {errors.governorate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.governorate}
                  </p>
                )}
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("city")} <span className="text-red-500">*</span>
                </label>
                <CitySelect
                  value={formData.area_id}
                  governorate={mapGovernorateToApiValue(formData.governorate) || formData.governorate}
                  onChange={(id) => handleInputChange("area_id", id)}
                  isRTL={isRTL}
                  disabled={!formData.governorate}
                  error={errors.area_id}
                  placeholder={t("select_city") || "Select City"}
                  loadingText={t("loading") || "Loading..."}
                  displayName={isRTL ? formData.area_name_ar : formData.area_name}
                />
                {errors.area_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.area_id}</p>
                )}
              </div>
            </div>

            {/* Neighborhood, Street and House Number */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("neighborhood")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.neighborhood}
                  onChange={(e) => {
                    const converted = normalizeNumericInput(e.target.value);
                    handleInputChange(
                      "neighborhood",
                      parseInt(converted) || 0
                    );
                  }}
                  placeholder={t("neighborhood_placeholder")}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.neighborhood
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.neighborhood && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.neighborhood}
                  </p>
                )}
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("street")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.street}
                  onChange={(e) => {
                    const converted = normalizeNumericInput(e.target.value);
                    handleInputChange("street", parseInt(converted) || 0);
                  }}
                  placeholder={t("street_placeholder")}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.street
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.street && (
                  <p className="text-red-500 text-sm mt-1">{errors.street}</p>
                )}
              </div>
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("house_number")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.house_number}
                  onChange={(e) => {
                    const converted = normalizeNumericInput(e.target.value);
                    handleInputChange(
                      "house_number",
                      parseInt(converted) || 0
                    );
                  }}
                  placeholder={t("house_number_placeholder")}
                  className={`w-full px-3 py-3 border rounded-lg transition-colors duration-200 ${
                    errors.house_number
                      ? "border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.house_number && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.house_number}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Clinic Information Section */}
          <div className="bg-pink-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {t("clinic_information")}
            </h3>

            {/* Radio button questions */}
            <div className="space-y-6">
              {/* Has Another Clinic */}
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-3 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("has_other_clinic")} <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="has_another_clinic"
                      value="yes"
                      checked={formData.has_another_clinic === true}
                      onChange={() => handleInputChange("has_another_clinic", true)}
                      className="w-4 h-4 text-[#2E5D7D] border-gray-300 focus:ring-[#2E5D7D] cursor-pointer"
                    />
                    <span className={`text-sm font-medium text-gray-700 ${isRTL ? "mr-2" : "ml-2"}`}>
                      {t("yes")}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="has_another_clinic"
                      value="no"
                      checked={formData.has_another_clinic === false}
                      onChange={() => handleInputChange("has_another_clinic", false)}
                      className="w-4 h-4 text-[#2E5D7D] border-gray-300 focus:ring-[#2E5D7D] cursor-pointer"
                    />
                    <span className={`text-sm font-medium text-gray-700 ${isRTL ? "mr-2" : "ml-2"}`}>
                      {t("no")}
                    </span>
                  </label>
                </div>
                {errors.has_another_clinic && (
                  <p className="text-red-500 text-sm mt-2">{errors.has_another_clinic}</p>
                )}
              </div>

              {/* Is Supervising a Medical Center */}
              <div>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-3 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {t("supervises_medical_center")} <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="is_supervising_a_medical_center"
                      value="yes"
                      checked={formData.is_supervising_a_medical_center === true}
                      onChange={() => handleInputChange("is_supervising_a_medical_center", true)}
                      className="w-4 h-4 text-[#2E5D7D] border-gray-300 focus:ring-[#2E5D7D] cursor-pointer"
                    />
                    <span className={`text-sm font-medium text-gray-700 ${isRTL ? "mr-2" : "ml-2"}`}>
                      {t("yes")}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="is_supervising_a_medical_center"
                      value="no"
                      checked={formData.is_supervising_a_medical_center === false}
                      onChange={() => handleInputChange("is_supervising_a_medical_center", false)}
                      className="w-4 h-4 text-[#2E5D7D] border-gray-300 focus:ring-[#2E5D7D] cursor-pointer"
                    />
                    <span className={`text-sm font-medium text-gray-700 ${isRTL ? "mr-2" : "ml-2"}`}>
                      {t("no")}
                    </span>
                  </label>
                </div>
                {errors.is_supervising_a_medical_center && (
                  <p className="text-red-500 text-sm mt-2">{errors.is_supervising_a_medical_center}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResponsiveFormLayout>
  );
};

export default DoctorInfoRegistrationScreen;
